import React from 'react';
import { MapPinIcon } from '@heroicons/react/24/outline';

interface InteractiveMapProps {
  address: string;
  coordinates: {
    latitude: number;
    longitude: number;
  };
  companyName: string;
  description: string;
  height?: string;
}

const InteractiveMap: React.FC<InteractiveMapProps> = ({
  address,
  coordinates,
  companyName,
  description,
  height = 'h-64'
}) => {
  const openInGoogleMaps = () => {
    const encodedAddress = encodeURIComponent(address);
    window.open(`https://www.google.com/maps/search/${encodedAddress}/@${coordinates.latitude},${coordinates.longitude},15z`, '_blank');
  };

  const openIn2GIS = () => {
    window.open(`https://2gis.kz/uralsk/search/${encodeURIComponent(address)}`, '_blank');
  };

  const openInYandexMaps = () => {
    window.open(`https://yandex.kz/maps/?text=${encodeURIComponent(address)}&ll=${coordinates.longitude},${coordinates.latitude}&z=15`, '_blank');
  };

  return (
    <div className={`relative ${height} rounded-lg overflow-hidden shadow-lg`}>
      {/* Интерактивная карта */}
      <div 
        className="w-full h-full bg-gray-100 cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={openInGoogleMaps}
      >
        <div className="relative w-full h-full bg-gradient-to-br from-blue-50 to-green-50">
          {/* Паттерн карты */}
          <div className="absolute inset-0 opacity-20">
            <div className="w-full h-full" style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23cbd5e0' fill-opacity='0.3'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
              backgroundSize: '60px 60px'
            }}>
            </div>
          </div>
          
          {/* Сетка улиц */}
          <div className="absolute inset-0 opacity-10">
            <svg className="w-full h-full" viewBox="0 0 400 300">
              <line x1="0" y1="100" x2="400" y2="100" stroke="#64748b" strokeWidth="2"/>
              <line x1="0" y1="200" x2="400" y2="200" stroke="#64748b" strokeWidth="2"/>
              <line x1="100" y1="0" x2="100" y2="300" stroke="#64748b" strokeWidth="2"/>
              <line x1="200" y1="0" x2="200" y2="300" stroke="#64748b" strokeWidth="2"/>
              <line x1="300" y1="0" x2="300" y2="300" stroke="#64748b" strokeWidth="2"/>
            </svg>
          </div>
          
          {/* Главный маркер */}
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10">
            <div className="relative group">
              <MapPinIcon className="h-8 w-8 text-red-500 drop-shadow-lg group-hover:scale-110 transition-transform" />
              <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
              
              {/* Tooltip при наведении */}
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                <div className="bg-black text-white text-xs rounded py-1 px-2 whitespace-nowrap">
                  {companyName}
                </div>
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-black"></div>
              </div>
            </div>
          </div>
          
          {/* Координаты */}
          <div className="absolute top-4 right-4 bg-white rounded-lg px-3 py-2 text-xs text-gray-600 shadow-md">
            {coordinates.latitude.toFixed(4)}° N, {coordinates.longitude.toFixed(4)}° E
          </div>
          
          {/* Масштаб */}
          <div className="absolute bottom-4 right-4 bg-white rounded text-xs px-2 py-1 text-gray-600 shadow-md">
            Zoom: 15
          </div>
          
          {/* Кнопки для открытия в картах */}
          <div className="absolute top-4 left-4 space-y-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                openInGoogleMaps();
              }}
              className="block bg-white hover:bg-gray-50 rounded-lg px-3 py-2 text-xs text-gray-700 shadow-md transition-colors"
            >
              📍 Google Maps
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                openIn2GIS();
              }}
              className="block bg-white hover:bg-gray-50 rounded-lg px-3 py-2 text-xs text-gray-700 shadow-md transition-colors"
            >
              🗺️ 2GIS
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                openInYandexMaps();
              }}
              className="block bg-white hover:bg-gray-50 rounded-lg px-3 py-2 text-xs text-gray-700 shadow-md transition-colors"
            >
              🌍 Яндекс.Карты
            </button>
          </div>
        </div>
      </div>
      
      {/* Информационная панель */}
      <div className="absolute bottom-4 left-4 right-4 pointer-events-none">
        <div className="bg-white rounded-lg shadow-lg p-4">
          <div className="flex items-center">
            <div className="w-3 h-3 bg-red-500 rounded-full mr-3 animate-pulse"></div>
            <div>
              <p className="font-medium text-gray-900">{companyName}</p>
              <p className="text-gray-600 text-sm">{description}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InteractiveMap;